import { useId, type MouseEvent } from "react";
import { ExternalLink } from "lucide-react";
import { Card, Monogram } from "@codelittinc/carbon-design-system";
import type { Tool } from "@carbon-os/server/contract";
import { trackToolClick } from "../lib/analytics";
import { isHttpUrl, toolHint, toolLogoUrl } from "../lib/tools";
import { TicketLink } from "./TicketLink";

const LAYOUT = "flex min-h-[72px] items-start gap-3";

/** The whole card is the link, so on a phone the target is the full width. */
export function ToolCard({ tool, accessRequestUrl }: { tool: Tool; accessRequestUrl: string | null }) {
  const hintId = useId();
  const hint = toolHint(tool);

  const body = (
    <>
      {/* The logo, or the letters when there is none, is decoration; the name is the label. */}
      <Monogram name={tool.name} src={toolLogoUrl(tool.key)} />
      <div className="min-w-0 flex-1">
        <h2 className="line-clamp-2 text-sm font-medium break-words text-text-primary">{tool.name}</h2>
        <p id={hintId} className="truncate text-xs text-text-secondary">
          {hint}
        </p>
        {!isHttpUrl(tool.url) && accessRequestUrl ? (
          <TicketLink href={accessRequestUrl} className="h-11 px-0 sm:h-auto" via="tool_unavailable" toolKey={tool.key} />
        ) : null}
      </div>
    </>
  );

  if (!isHttpUrl(tool.url)) {
    return (
      <Card className={LAYOUT}>
        {body}
      </Card>
    );
  }

  // Counted, never steered: the browser opens the tab exactly as it would anyway.
  // `click` covers a plain, modified or keyboard open; a middle click only
  // fires `auxclick`.
  const onAuxClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button === 1) trackToolClick(tool, "card");
  };

  return (
    <Card
      asChild
      hoverable
      className={`group ${LAYOUT} focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:outline-none motion-reduce:transition-none`}
    >
      <a
        href={tool.url}
        target="_blank"
        rel="noreferrer"
        aria-label={`Open ${tool.name} (opens in a new tab)`}
        aria-describedby={hintId}
        onClick={() => trackToolClick(tool, "card")}
        onAuxClick={onAuxClick}
      >
        {body}
        <ExternalLink
          aria-hidden
          className="size-4 shrink-0 text-text-secondary group-hover:text-accent-text"
        />
      </a>
    </Card>
  );
}
